import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useActiveOffer } from '../../offers/useOffer';
import { isEligible } from '../../offers/offerEngine';

const COLOR_MAP = {
  black: '#000000', white: '#ffffff', 'OFF-White': '#FAF9F6', Jade: '#00A86B',
  red: '#ef4444', green: '#22c55e', yellow: '#facc15', gray: '#64748b',
  pink: '#f472b6', beige: '#f5f5dc', navy: '#000080', blue: '#2563eb',
  lightblue: '#add8e6', 'light blue': '#add8e6', 'Navy Blue': '#081820',
  'Petrol Blue': '#0c3238', 'Royal Blue': '#141c45', 'Olive Green': '#2e2e0d',
  'Golden Yellow': '#ffa100', 'Mustard Yellow': '#cf9307', 'Bottle Green': '#0a3818',
  'Choral': '#9e413e', 'Grey Melange': '#cccac9', 'Charcoal Grey': '#36454F', 'Melange Grey': '#cccac9', 'sunset saga': '#FD9C79',
  'urban forest': '#184A45', 'midnight muse': '#232946', 'cotton cloud': '#E9EAEC',  'Light Baby Pink': '#FBD9D9',  'Mint': '#B6FFBB',
  'Dark Wash': '#2C3E50', brown: '#8B4513', lavender: '#E6E6FA',
  'Baby Blue': '#89CFF0', mint: '#3EB489', 'light baby pink': '#FFD1DF',
  'White Black': 'linear-gradient(45deg, #ffffff 50%, #000000 50%)',
  'Brown Black': 'linear-gradient(45deg, #8B4513 50%, #000000 50%)',
  'White Lavender': 'linear-gradient(45deg, #ffffff 50%, #E6E6FA 50%)',
  'Black - White': 'linear-gradient(45deg, #000000 50%, #ffffff 50%)',
  'Black - Charcoal Melange': 'linear-gradient(45deg, #000000 50%, #4a4a4a 50%)',
  'White - Black': 'linear-gradient(45deg, #ffffff 50%, #000000 50%)',
  'Maroon - OFF White': 'linear-gradient(45deg, #800000 50%, #F8F8F8 50%)',
  'Mustard Yellow - OFF White': 'linear-gradient(45deg, #D4A017 50%, #F8F8F8 50%)',
  'Coffee Brown - OFF White': 'linear-gradient(45deg, #6F4E37 50%, #F8F8F8 50%)',
  'Olive Green - OFF White': 'linear-gradient(45deg, #556B2F 50%, #F8F8F8 50%)',
  'Black - Charcoal Melange': 'linear-gradient(45deg, #000000 50%, #4A4A4A 50%)',
};

/* ── IMAGE OPTIMISATION ──
   Cloudinary lets us ask for a smaller, better-compressed copy of the same
   image just by adding a transformation segment to the URL. The segment is
   inserted AFTER any existing transformation (e.g. t_VESHH-EON_WEBSITE) and
   before the version / file path, so it can only ever shrink the image, never
   upscale it. Non-Cloudinary and signed URLs are left as-is. If a resized URL
   fails to load, the card falls back to the original URL once. */
const CLD_MARKER = '/image/upload/';
const IMG_WIDTHS = [320, 480, 640];       // few distinct sizes = better CDN cache hits
const IMG_DEFAULT_W = 480;
const IMG_QUALITY = 'q_auto:eco';         // small files; use 'q_auto' or 'q_auto:good' for crisper
const IMG_SIZES = '(max-width: 1024px) 50vw, 25vw'; // 2 cols on tablet/mobile, 4 on desktop
const PRIORITY_COUNT = 4;                 // first row: high priority, fetched first
const EAGER_LIMIT = 60;                   // every image up to here starts downloading right away
                                          // (low priority, so it never slows the first row);
                                          // only a very long tail falls back to native lazy

// Cloudinary transformation parameter keys. A path segment made only of "key_value" parts with these
// keys (e.g. t_NAME, f_auto,q_auto, w_800) is an existing transformation; anything else (v123456, a
// folder, the file name) is where the image path starts.
const CLD_KEYS = new Set(['a','ac','af','ar','b','bo','c','co','d','dl','dn','dpr','du','e','eo','f','fl','fn','g','h','ki','l','o','p','pg','q','r','so','sp','t','u','vc','vs','w','x','y','z']);
const isTransformSegment = (seg) =>
  seg.split(',').every((part) => {
    const i = part.indexOf('_');
    return i > 0 && CLD_KEYS.has(part.slice(0, i));
  });

const cldResize = (url, width) => {
  if (!url || typeof url !== 'string' || !url.includes('res.cloudinary.com')) return url;
  const i = url.indexOf(CLD_MARKER);
  if (i === -1) return url;
  const start = i + CLD_MARKER.length;
  const segs = url.slice(start).split('/');
  if (segs[0].startsWith('s--')) return url; // signed URL: adding anything would invalidate it
  let k = 0;
  while (k < segs.length - 1 && isTransformSegment(segs[k])) k++; // never treat the last segment (file) as a transformation
  segs.splice(k, 0, `f_auto,${IMG_QUALITY},w_${width},c_limit`);
  return url.slice(0, start) + segs.join('/');
};

const buildSrcSet = (url) => {
  if (cldResize(url, IMG_WIDTHS[0]) === url) return undefined;
  return IMG_WIDTHS.map((w) => `${cldResize(url, w)} ${w}w`).join(', ');
};

const calculateDiscount = (original, current) => {
  if (!original || original <= current) return 0;
  return Math.round(((original - current) / original) * 100);
};

const getProductImage = (product, index = 0) => {
  const images = product.images || product.image || [];
  return images[index]?.url || null;
};

/* ── CARD ──
   Memoised and owns its own hover state, so hovering one card no longer
   re-renders the whole grid. */
const ProductCard = memo(function ProductCard({ product, index, bogo }) {
  const [isHovered, setIsHovered] = useState(false);
  const [wantSecond, setWantSecond] = useState(false);   // mount hover image only after first hover
  const [secondReady, setSecondReady] = useState(false); // swap only once it has actually loaded
  const [primaryRaw, setPrimaryRaw] = useState(false);   // fall back to original URL if optimised one fails
  const [secondRaw, setSecondRaw] = useState(false);

  const url0 = getProductImage(product, 0);
  const url1 = getProductImage(product, 1);
  const hasSecondImage = !!url1;
  const showSecond = isHovered && hasSecondImage && secondReady;

  const discount = calculateDiscount(product.originalPrice, product.price);
  const colors = product.colors || product.variants?.filter(v => v.type === 'color') || [];
  const isOutOfStock = product.inStock === false;
  const isLowStock = product.stock && product.stock < 10 && product.stock > 0;
  const isPriority = index < PRIORITY_COUNT;

  const srcProps = (url, raw) => {
    if (raw) return { src: url };
    const srcSet = buildSrcSet(url);
    return srcSet
      ? { src: cldResize(url, IMG_DEFAULT_W), srcSet, sizes: IMG_SIZES }
      : { src: url };
  };

  return (
    <div
      className="pg-card"
      onMouseEnter={() => { setIsHovered(true); setWantSecond(true); }}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to={`/product/${product._id}`} className="pg-card-link">

        {/* ── IMAGE ── */}
        <div className="pg-img-wrap">
          {url0 ? (
            <>
              <img
                {...srcProps(url0, primaryRaw)}
                alt={product.images?.[0]?.altText || product.name}
                className="pg-img pg-img-primary"
                style={{
                  opacity: showSecond ? 0 : 1,
                  transform: showSecond ? 'scale(1.06)' : 'scale(1)',
                }}
                loading={index < EAGER_LIMIT ? 'eager' : 'lazy'}
                fetchPriority={isPriority ? 'high' : 'low'}
                decoding="async"
                onError={(e) => {
                  if (!primaryRaw && cldResize(url0, IMG_DEFAULT_W) !== url0) setPrimaryRaw(true);
                  else e.target.style.display = 'none';
                }}
                draggable="false"
              />
              {hasSecondImage && wantSecond && (
                <img
                  {...srcProps(url1, secondRaw)}
                  alt={product.images?.[1]?.altText || product.name}
                  className="pg-img pg-img-secondary"
                  style={{
                    opacity: showSecond ? 1 : 0,
                    transform: showSecond ? 'scale(1.04)' : 'scale(1)',
                  }}
                  decoding="async"
                  fetchPriority="high"
                  onLoad={() => setSecondReady(true)}
                  onError={() => {
                    if (!secondRaw && cldResize(url1, IMG_DEFAULT_W) !== url1) setSecondRaw(true);
                  }}
                  draggable="false"
                />
              )}
            </>
          ) : (
            <div className="pg-no-img">
              <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'rgba(255,255,255,0.15)' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}

          {/* Badges */}
          <div className="pg-badges">
            {bogo && (
              <span className="pg-badge pg-badge-bogo">BOGO</span>
            )}
            {discount > 0 && (
              <span className="pg-badge pg-badge-discount">-{discount}%</span>
            )}
            {product.isNew && (
              <span className="pg-badge pg-badge-new">New</span>
            )}
            {product.isBestSeller && (
              <span className="pg-badge pg-badge-best">Best Seller</span>
            )}
          </div>

          {/* Stock overlay on image */}
          {isOutOfStock && (
            <div className="pg-overlay-stock pg-sold-out">Sold Out</div>
          )}
          {!isOutOfStock && isLowStock && (
            <div className="pg-overlay-stock pg-low-stock">
              Only {product.stock} left
            </div>
          )}
        </div>

        {/* ── CAPTION ── */}
        <div className="pg-caption">
          <div className="pg-caption-row">
            {/* Name — wraps freely */}
            <span className="pg-name">{product.name}</span>
            {/* Price */}
            <div className="pg-price-wrap">
              <span className="pg-price">
                ₹{typeof product.price === 'number' ? product.price.toFixed(0) : product.price}
              </span>
              {product.originalPrice && product.originalPrice > product.price && (
                <span className="pg-original-price">
                  ₹{typeof product.originalPrice === 'number' ? product.originalPrice.toFixed(0) : product.originalPrice}
                </span>
              )}
            </div>
          </div>

          {/* Stock text in caption */}
          {isOutOfStock && (
            <span className="pg-stock-tag pg-stock-out">Out of Stock</span>
          )}
          {!isOutOfStock && isLowStock && (
            <span className="pg-stock-tag pg-stock-low">Only {product.stock} left!</span>
          )}

          {/* Color swatches */}
          {colors.length > 0 && (
            <div className="pg-colors">
              {colors.slice(0, 5).map((color, i) => {
                const colorName = color.name || color;
                const colorValue = color.value || color.code || COLOR_MAP[colorName] || colorName;
                const isGradient = typeof colorValue === 'string' && colorValue.startsWith('linear-gradient');
                return (
                  <div
                    key={i}
                    className="pg-swatch"
                    style={isGradient ? { background: colorValue } : { backgroundColor: colorValue }}
                    title={colorName}
                  />
                );
              })}
              {colors.length > 5 && (
                <span className="pg-colors-more">+{colors.length - 5}</span>
              )}
            </div>
          )}
        </div>

      </Link>
    </div>
  );
});

const ProductGrid = ({ products, loading, error }) => {
  const location = useLocation();
  const { offer: activeOffer } = useActiveOffer(); // BOGO badge follows the live offer automatically
  const gridRef = useRef(null);
  const userInteractedRef = useRef(false);

  /* ── SORT (memoised: only re-sorts when products change) ── */
  const sortedProducts = useMemo(() => {
    if (!products || products.length === 0) return [];
    return products
      .map((p) => [new Date(p.updatedAt || p.createdAt || 0).getTime(), p])
      .sort((a, b) => b[0] - a[0])
      .map(([, p]) => p);
  }, [products]);

  /* ── SCROLL POSITION FIX ──
     Prevents the browser's native scroll restoration from clamping to the
     bottom of the page when navigating back here while this grid is still
     showing its (short) loading skeleton. We take over restoration manually:
     save scroll position per-URL as the user scrolls, and only restore it
     once real product data (not the skeleton) has actually rendered. */
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    userInteractedRef.current = false;
    const markInteracted = () => { userInteractedRef.current = true; };
    window.addEventListener('wheel', markInteracted, { passive: true });
    window.addEventListener('touchstart', markInteracted, { passive: true });
    window.addEventListener('keydown', markInteracted);
    return () => {
      window.removeEventListener('wheel', markInteracted);
      window.removeEventListener('touchstart', markInteracted);
      window.removeEventListener('keydown', markInteracted);
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    const scrollKey = `pg-scroll:${location.pathname}${location.search}`;
    const handleScroll = () => {
      sessionStorage.setItem(scrollKey, String(window.scrollY));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (loading) return; // wait until real content (not skeleton) is rendered
    const scrollKey = `pg-scroll:${location.pathname}${location.search}`;
    const saved = sessionStorage.getItem(scrollKey);
    if (saved === null) return;

    const targetY = parseInt(saved, 10);
    const applyScroll = () => {
      if (userInteractedRef.current) return;
      window.scrollTo(0, targetY);
    };

    // First attempt, once the browser has painted the real grid.
    requestAnimationFrame(() => requestAnimationFrame(applyScroll));

    // Layout can still shift after that first paint (images finishing
    // decode, the Exo 2 web font swapping in, etc.), which is what was
    // throwing the restored position off. Keep correcting until the
    // grid's height stops changing, then stop.
    const node = gridRef.current;
    if (!node || !('ResizeObserver' in window)) return;

    let settleTimer = null;
    let safetyTimer = null;

    const observer = new ResizeObserver(() => {
      if (userInteractedRef.current) {
        observer.disconnect();
        return;
      }
      clearTimeout(settleTimer);
      settleTimer = setTimeout(applyScroll, 60);
    });
    observer.observe(node);

    // Hard stop so we're never correcting scroll indefinitely.
    safetyTimer = setTimeout(() => observer.disconnect(), 2000);

    return () => {
      clearTimeout(settleTimer);
      clearTimeout(safetyTimer);
      observer.disconnect();
    };
  }, [loading, location.pathname, location.search]);

  /* ── LOADING ── */
  if (loading) {
    return (
      <>
        <style>{pgStyles}</style>
        <div className="pg-grid">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="pg-skeleton-card">
              <div className="pg-skeleton" />
              <div className="pg-skeleton-cap" />
            </div>
          ))}
        </div>
      </>
    );
  }

  /* ── ERROR ── */
  if (error) {
    return (
      <>
        <style>{pgStyles}</style>
        <div className="pg-state">
          <span className="pg-state-label">Failed to load products</span>
          <span className="pg-state-sub">{error}</span>
        </div>
      </>
    );
  }

  /* ── EMPTY ── */
  if (!products || products.length === 0) {
    return (
      <>
        <style>{pgStyles}</style>
        <div className="pg-state">
          <span className="pg-state-label">No products found</span>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{pgStyles}</style>
      <div className="pg-grid" ref={gridRef}>
        {sortedProducts.map((product, index) => (
          <ProductCard
            key={product._id}
            product={product}
            index={index}
            bogo={!!activeOffer && isEligible(activeOffer.eligible, product._id)}
          />
        ))}
      </div>
    </>
  );
};

/* ── STYLES ── */
const pgStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');

  .pg-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2px;
    margin-top: 24px;
    font-family: 'Exo 2', sans-serif;
  }

  /* ── SKELETON ── */
  .pg-skeleton-card { background: #f4f4f4; }
  .pg-skeleton {
    aspect-ratio: 3 / 4;
    background: #e8e8e8;
    position: relative;
    overflow: hidden;
  }
  .pg-skeleton-cap { height: 44px; background: #f4f4f4; }
  .pg-skeleton::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.7) 50%, transparent 100%);
    animation: pg-shimmer 1.6s infinite;
  }
  @keyframes pg-shimmer {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  /* ── STATE ── */
  .pg-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 0;
    gap: 8px;
    font-family: 'Exo 2', sans-serif;
  }
  .pg-state-label {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.4);
  }
  .pg-state-sub {
    font-size: 10px;
    font-weight: 300;
    color: rgba(255,255,255,0.2);
    letter-spacing: 0.1em;
  }

  /* ── CARD ── */
  .pg-card {
    position: relative;
    background: #0a0a0a;
    overflow: hidden;
  }

  .pg-card-link {
    display: block;
    text-decoration: none;
  }

  /* ── IMAGE ── */
  .pg-img-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 3 / 4;
    overflow: hidden;
    background: #111;
  }

  .pg-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: opacity 0.5s ease, transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  .pg-img-secondary { z-index: 1; }

  .pg-no-img {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #111;
  }

  /* ── BADGES ── */
  .pg-badges {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 3;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .pg-badge {
    font-family: 'Exo 2', sans-serif;
    font-size: 7px;
    font-weight: 600;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    padding: 3px 8px;
    display: inline-block;
  }
  .pg-badge-bogo     { background: #000; color: #fff; }
  .pg-badge-discount { background: #fff; color: #000; }
  .pg-badge-new      { background: #000; color: #fff; outline: 1px solid rgba(255,255,255,0.3); }
  .pg-badge-best     { background: #fff; color: #000; }

  /* ── STOCK OVERLAYS on image ── */
  .pg-overlay-stock {
    position: absolute;
    bottom: 0;
    left: 0; right: 0;
    z-index: 3;
    font-family: 'Exo 2', sans-serif;
    font-size: 8px;
    font-weight: 500;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    text-align: center;
    padding: 6px;
  }
  .pg-sold-out { background: rgba(0,0,0,0.82); color: rgba(255,255,255,0.4); }
  .pg-low-stock { background: rgba(0,0,0,0.72); color: rgba(255,255,255,0.65); }

  /* ── CAPTION ── */
  .pg-caption {
  background: #f8f8f8;
  padding: 10px 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-top: 1px solid black;
  border-bottom: 1px solid black;
}

  .pg-caption-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
  }

  /* Name wraps freely — no truncation */
  .pg-name {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #000;
    line-height: 1.5;
    word-break: break-word;
  }

  .pg-price-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
    gap: 2px;
  }

  .pg-price {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.1em;
    color: #000;
    white-space: nowrap;
  }

  .pg-original-price {
    font-size: 10px;
    font-weight: 300;
    color: rgba(0,0,0,0.35);
    text-decoration: line-through;
    letter-spacing: 0.05em;
    white-space: nowrap;
  }

  /* Stock text in caption */
  .pg-stock-tag {
    font-family: 'Exo 2', sans-serif;
    font-size: 8px;
    font-weight: 500;
    letter-spacing: 0.25em;
    text-transform: uppercase;
  }
  .pg-stock-out { color: rgba(0,0,0,0.35); }
  .pg-stock-low { color: #b45309; }

  /* ── COLOR SWATCHES ── */
  .pg-colors {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .pg-swatch {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 1px solid rgba(0,0,0,0.15);
    flex-shrink: 0;
    cursor: pointer;
    transition: border-color 0.2s ease;
  }
  .pg-swatch:hover { border-color: rgba(0,0,0,0.5); }
  .pg-colors-more {
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.1em;
    color: rgba(0,0,0,0.35);
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .pg-grid { grid-template-columns: repeat(2, 1fr); }
  }

  @media (max-width: 768px) {
    .pg-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
      margin-top: 16px;
    }
    .pg-name  { font-size: 9px; letter-spacing: 0.15em; }
    .pg-price { font-size: 11px; }
    .pg-caption { padding: 8px 8px 10px; gap: 4px; }
    .pg-badge { font-size: 6px; padding: 2px 6px; }
  }

  @media (max-width: 360px) {
    .pg-grid { gap: 6px; }
  }
`;

export default ProductGrid;
